import { useQuery } from '@tanstack/react-query';
import { coachingService } from '@/services/domains/coaching/coaching.service';
import { sessionsService } from '@/services/domains/coaching/sessions.service';
import { exercisesService } from '@/services/domains/coaching/exercises.service';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import type {
  CoachProfile,
  CoachingSession,
  StudentSummary,
  CoachDashboardData,
} from '@/types/coaching.types';

// Extended student summary with session stats
export interface StudentWithStats extends StudentSummary {
  session_count: number;
  sessions_this_month: number;
  handicap_change?: number;
  previous_handicap?: number;
}

interface UseCoachDashboardResult {
  profile: CoachProfile | null;
  students: StudentWithStats[];
  upcomingSessions: CoachingSession[];
  stats: {
    active_students: number;
    sessions_this_week: number;
    sessions_this_month: number;
    total_sessions: number;
    pending_reviews: number;
    homework_completion_rate: number;
    avg_rating: number | null;
  };
  isLoading: boolean;
  studentsLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useCoachDashboard(clubId?: string | null): UseCoachDashboardResult {
  const { user } = useAuth();

  // Get coach profile - returns null if none exists (user needs to create one)
  const profileQuery = useQuery({
    queryKey: ['coach-profile', user?.id],
    queryFn: async () => {
      const response = await coachingService.getMyProfile();
      if (!response.success) {
        throw new Error(response.error || 'Failed to load coach profile');
      }
      return response.data || null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get students list
  const studentsQuery = useQuery({
    queryKey: ['coach-students', profileQuery.data?.id, clubId ?? null],
    queryFn: async () => {
      const response = await coachingService.getStudents({ status: 'active', club_id: clubId ?? undefined });
      if (!response.success) {
        throw new Error(response.error || 'Failed to load students');
      }
      return response.data || [];
    },
    enabled: !!profileQuery.data?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get year-to-date sessions so the schedule calendar is fully populated (past + future)
  const sessionsQuery = useQuery({
    queryKey: ['coach-ytd-sessions', profileQuery.data?.id],
    queryFn: async () => {
      if (!profileQuery.data?.id) return [];
      const response = await sessionsService.getYearToDateSessions(profileQuery.data.id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load sessions');
      }
      return response.data || [];
    },
    enabled: !!profileQuery.data?.id,
    staleTime: 2 * 60 * 1000,
  });

  // Get pending exercise reviews and homework stats
  const exerciseStatsQuery = useQuery({
    queryKey: ['coach-exercise-stats', profileQuery.data?.id],
    queryFn: async () => {
      if (!profileQuery.data?.id) return { pending_reviews: 0, completion_rate: 0 };

      // Get all assignments for this coach
      const allResponse = await exercisesService.getCoachAssignments(profileQuery.data.id, {});
      const assignments = allResponse.data || [];

      // Calculate pending reviews (completed but no coach notes)
      const pending_reviews = assignments.filter(a => a.status === 'completed' && !a.coach_notes).length;

      // Calculate completion rate
      const completed = assignments.filter(a => a.status === 'completed').length;
      const total = assignments.length;
      const completion_rate = total > 0 ? Math.round((completed / total) * 100) : 0;

      return { pending_reviews, completion_rate };
    },
    enabled: !!profileQuery.data?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get session statistics (completed sessions this month and all time)
  const sessionStatsQuery = useQuery({
    queryKey: ['coach-session-stats', profileQuery.data?.id],
    queryFn: async () => {
      if (!profileQuery.data?.id) return { this_month: 0, total: 0, by_student: {} as Record<string, { total: number; this_month: number }> };

      const now = new Date();
      const monthStart = startOfMonth(now).toISOString();
      const monthEnd = endOfMonth(now).toISOString();

      // Run total count and this-month rows in parallel — no unbounded full-table scan
      const [totalResult, monthResult] = await Promise.all([
        supabase
          .from('coaching_sessions')
          .select('id', { count: 'exact', head: true })
          .eq('coach_id', profileQuery.data.id)
          .eq('status', 'completed'),
        supabase
          .from('coaching_sessions')
          .select('player_id')
          .eq('coach_id', profileQuery.data.id)
          .eq('status', 'completed')
          .gte('scheduled_at', monthStart)
          .lte('scheduled_at', monthEnd),
      ]);

      if (totalResult.error) throw totalResult.error;
      if (monthResult.error) throw monthResult.error;

      const total = totalResult.count ?? 0;
      const monthSessions = monthResult.data || [];
      const this_month = monthSessions.length;

      // Sessions this month by student (only month rows needed for the enrichment map)
      const by_student: Record<string, { total: number; this_month: number }> = {};
      monthSessions.forEach(s => {
        if (!by_student[s.player_id]) {
          by_student[s.player_id] = { total: 0, this_month: 0 };
        }
        by_student[s.player_id].this_month++;
      });

      return { this_month, total, by_student };
    },
    enabled: !!profileQuery.data?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get handicap history for students to calculate progression
  const handicapHistoryQuery = useQuery({
    queryKey: ['coach-student-handicaps', profileQuery.data?.id, studentsQuery.data?.map(s => s.user?.id || s.id)],
    queryFn: async () => {
      if (!studentsQuery.data?.length) return {};

      const lastMonth = subMonths(new Date(), 1).toISOString();
      const handicapChanges: Record<string, { current: number | null; previous: number | null }> = {};

      // Build userId → studentId map
      const userIdToStudentId: Record<string, string> = {};
      studentsQuery.data.forEach(student => {
        const userId = student.user?.id;
        if (userId) userIdToStudentId[userId] = student.id;
      });
      const userIds = Object.keys(userIdToStudentId);
      if (!userIds.length) return {};

      // Batch query 1: all memberships for all students in one round-trip
      // (previously: one query per student)
      const { data: memberships } = await supabase
        .from('user_club_memberships')
        .select('id, user_id')
        .in('user_id', userIds);

      if (!memberships?.length) {
        studentsQuery.data.forEach(student => {
          handicapChanges[student.id] = { current: student.handicap, previous: null };
        });
        return handicapChanges;
      }

      // Deduplicate: one membership per user_id (first row wins)
      const membershipByUserId: Record<string, string> = {};
      memberships.forEach(m => {
        if (!membershipByUserId[m.user_id]) {
          membershipByUserId[m.user_id] = m.id;
        }
      });
      const membershipIds = Object.values(membershipByUserId);

      // Batch query 2: all historical records for all memberships in one round-trip
      // (previously: one query per student)
      const { data: records } = await supabase
        .from('playing_activity_records')
        .select('member_id, handicap_at_time, date_played')
        .in('member_id', membershipIds)
        .lt('date_played', lastMonth)
        .order('date_played', { ascending: false });

      // Pick most recent record per membership (rows are desc-ordered so first row wins)
      const latestRecordByMembershipId: Record<string, number | null> = {};
      (records || []).forEach(r => {
        if (!(r.member_id in latestRecordByMembershipId)) {
          latestRecordByMembershipId[r.member_id] = r.handicap_at_time ?? null;
        }
      });

      // Assemble result keyed by student.id — same shape as before
      studentsQuery.data.forEach(student => {
        const userId = student.user?.id;
        const membershipId = userId ? membershipByUserId[userId] : undefined;
        const previousHandicap = membershipId != null
          ? (latestRecordByMembershipId[membershipId] ?? null)
          : null;
        handicapChanges[student.id] = {
          current: student.handicap,
          previous: previousHandicap,
        };
      });

      return handicapChanges;
    },
    enabled: !!studentsQuery.data?.length,
    staleTime: 10 * 60 * 1000,
  });

  const baseStudents = studentsQuery.data || [];
  const sessions = sessionsQuery.data || [];
  const sessionStats = sessionStatsQuery.data || { this_month: 0, total: 0, by_student: {} };
  const handicapHistory = handicapHistoryQuery.data || {};
  const exerciseStats = exerciseStatsQuery.data || { pending_reviews: 0, completion_rate: 0 };

  // Derive per-student session totals from the YTD sessions already loaded (no extra query)
  const sessionCountByPlayer: Record<string, number> = {};
  sessions.forEach(s => {
    if (s.status !== 'cancelled') {
      sessionCountByPlayer[s.player_id] = (sessionCountByPlayer[s.player_id] || 0) + 1;
    }
  });

  // Enrich students with session stats and handicap changes
  const students: StudentWithStats[] = baseStudents.map(student => {
    const studentUserId = student.user?.id || '';
    const sessionsThisMonth = sessionStats.by_student[studentUserId]?.this_month || 0;
    const handicapData = handicapHistory[student.id] || { current: null, previous: null };

    const handicap_change = (handicapData.current !== null && handicapData.previous !== null)
      ? Number((handicapData.previous - handicapData.current).toFixed(1))
      : undefined;

    return {
      ...student,
      session_count: sessionCountByPlayer[studentUserId] || 0,
      sessions_this_month: sessionsThisMonth,
      handicap_change,
      previous_handicap: handicapData.previous ?? undefined
    };
  });

  const stats = {
    active_students: students.length,
    sessions_this_week: (() => {
      const now = new Date();
      const jsDay = now.getDay();
      const diffToMonday = jsDay === 0 ? -6 : 1 - jsDay;
      const weekStart = new Date(now); weekStart.setDate(now.getDate() + diffToMonday); weekStart.setHours(0, 0, 0, 0);
      const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23, 59, 59, 999);
      return sessions.filter(s => {
        const t = new Date(s.scheduled_at).getTime();
        return t >= weekStart.getTime() && t <= weekEnd.getTime() && s.status !== 'cancelled';
      }).length;
    })(),
    sessions_this_month: sessionStats.this_month,
    total_sessions: sessionStats.total,
    pending_reviews: exerciseStats.pending_reviews,
    homework_completion_rate: exerciseStats.completion_rate,
    avg_rating: null, // TODO: implement rating system
  };

  // isLoading = true only on first load (no cached data for profile/sessions/stats).
  // studentsLoading is kept separate so callers can show inline spinners during
  // club-filter switches without blanking the entire UI.
  const isLoading =
    profileQuery.isLoading ||
    sessionsQuery.isLoading ||
    sessionStatsQuery.isLoading;

  const studentsLoading = studentsQuery.isLoading || studentsQuery.isFetching;

  const error =
    profileQuery.error ||
    studentsQuery.error ||
    sessionsQuery.error ||
    sessionStatsQuery.error;

  const refetch = () => {
    profileQuery.refetch();
    studentsQuery.refetch();
    sessionsQuery.refetch();
    exerciseStatsQuery.refetch();
    sessionStatsQuery.refetch();
    handicapHistoryQuery.refetch();
  };

  return {
    profile: profileQuery.data || null,
    students,
    upcomingSessions: sessions,
    stats,
    isLoading,
    studentsLoading,
    error: error as Error | null,
    refetch,
  };
}

// Hook for coach dashboard data as a single object
export function useCoachDashboardData() {
  const dashboard = useCoachDashboard();

  const data: CoachDashboardData | null = dashboard.profile ? {
    profile: dashboard.profile,
    stats: dashboard.stats,
    upcoming_sessions: dashboard.upcomingSessions,
    recent_activity: [],
  } : null;

  return {
    data,
    isLoading: dashboard.isLoading,
    error: dashboard.error,
    refetch: dashboard.refetch,
  };
}
